// src/utils/constants/routes.js

/**
 * Application route constants
 * Centralized route definitions with helper functions for dynamic routes
 */
export const ROUTES = {
  // Public routes
  LOGIN: '/login',
  REGISTER: '/register',
  
  // Dashboard routes
  DASHBOARD: '/dashboard',
  ONBOARDING: '/dashboard/onboarding',
  MANAGER_DASHBOARD: '/dashboard/manager',
  INVESTOR_DASHBOARD: '/dashboard/investor',

  // Messaging routes 
  MANAGER_MESSAGING: '/dashboard/manager/messaging',
  MANAGER_MESSAGING_THREAD: '/dashboard/manager/messaging/thread/:threadId',
  
  // Manager routes
  MANAGER_PRODUCTS: '/dashboard/manager/products',
  MANAGER_EXPLORE: '/dashboard/manager/explore',
  MANAGER_FUNDRAISING: '/dashboard/manager/fundraising',
  MANAGER_ANALYTICS: '/dashboard/manager/analytics',
  MANAGER_METRICS: '/dashboard/manager/metrics',
  MANAGER_PERFORMANCE: '/dashboard/manager/performance',
  MANAGER_SETTINGS: '/dashboard/manager/settings',
  MANAGER_BILLING_SETTINGS: '/dashboard/manager/settings/billing',
  MANAGER_CONTACT: '/dashboard/manager/contact',
  
  // Product sub-routes
  MANAGER_PRODUCT_NEW: '/dashboard/manager/products/new',
  MANAGER_PRODUCT_DETAILS: '/dashboard/manager/products/:productId',
  MANAGER_PRODUCT_EDIT: '/dashboard/manager/products/:productId/edit',
  MANAGER_COINVESTMENTS: '/dashboard/manager/coinvestments',
  MANAGER_COINVESTMENT_DETAILS: '/dashboard/manager/coinvestments/:coInvestmentId',
  MANAGER_COINVESTMENT_NEW: '/dashboard/manager/coinvestments/new',
  MANAGER_COINVESTMENT_EDIT: '/dashboard/manager/coinvestments/:coInvestmentId/edit',
  MANAGER_COINVESTMENT_PARTICIPANTS: '/dashboard/manager/products/coinvestments/:id/participants',
  
  // Investor Relations sub-routes
  MANAGER_INVESTOR_INTEREST: '/dashboard/manager/investor-interest',
  MANAGER_INVESTOR_COMMUNICATIONS: '/dashboard/manager/investor-communications',
  MANAGER_INVESTOR_DETAILS: '/dashboard/manager/investor-relations/:investorId',
  MANAGER_INVESTOR_MEETINGS: '/dashboard/manager/investor-relations/meetings',
  MANAGER_INVESTOR_DOCUMENTS: '/dashboard/manager/investor-relations/documents',
  MANAGER_INVESTOR_PROFILE: '/dashboard/manager/investors/:investorId',
  
  // Investor routes
  INVESTOR_EXPLORE: '/dashboard/investor/explore',
  INVESTOR_WATCHLIST: '/dashboard/investor/watchlist',
  
  // Portfolio routes (new redesigned system)
  INVESTOR_PORTFOLIOS: '/dashboard/investor/portfolios',
  INVESTOR_PORTFOLIO_CREATE: '/dashboard/investor/portfolios/create',
  INVESTOR_PORTFOLIO_EDIT: '/dashboard/investor/portfolios/:id/edit',
  INVESTOR_PORTFOLIO_VIEW: '/dashboard/investor/portfolios/:id',
  INVESTOR_PORTFOLIO_ANALYZE: '/dashboard/investor/portfolios/analyze',
  
  // Legacy portfolio builder routes (deprecated - redirect to new system)
  INVESTOR_PORTFOLIO_BUILDER: '/dashboard/investor/portfolio-builder',
  INVESTOR_PERFORMANCE_ANALYSIS: '/dashboard/investor/portfolio-builder/analysis',
  
  INVESTOR_MESSAGING: '/dashboard/investor/messaging',
  INVESTOR_MESSAGING_THREAD: '/dashboard/investor/messaging/thread/:threadId',
  INVESTOR_SETTINGS: '/dashboard/investor/settings',
  
  // Opportunity routes
  INVESTOR_OPPORTUNITY_DEALSHEET: '/dashboard/investor/opportunities/:id',
  INVESTOR_OPPORTUNITY_DEALSHEET_V2: '/dashboard/investor/explore-v2/opportunity/:id',
  INVESTOR_FIRM_PROFILE: '/dashboard/investor/firms/:firmId',
  
  // Deprecated - kept for backward compatibility
  INVESTOR_COINVESTMENT_DETAILS: '/dashboard/investor/common/opportunities/:id', // DEPRECATED: Use INVESTOR_OPPORTUNITY_DEALSHEET
  
  EXPLORE_OPPORTUNITIES: '/investor/explore-opportunities',
  INVESTOR_CALENDAR_VIEW: '/dashboard/investor/explore/calendar',
  INVESTOR_GEOGRAPHIC_VIEW: '/dashboard/investor/explore/map',
  INVESTOR_TRENDING_VIEW: '/dashboard/investor/explore/trending',
  
  // Layout Demo (Temporary for testing)
  INVESTOR_LAYOUT_DEMO: '/dashboard/investor/layout-demo',

  // Investment Orders routes
  INVESTOR_ORDERS: '/dashboard/investor/orders',
  INVESTOR_ORDER_DETAILS: '/dashboard/investor/orders/:order_id',
  MANAGER_ORDERS: '/dashboard/manager/orders',
  MANAGER_ORDER_DETAILS: '/dashboard/manager/orders/:order_id',
  ADMIN_ORDERS: '/dashboard/admin/orders',
  ADMIN_ORDER_DETAILS: '/dashboard/admin/orders/:order_id',

  // API Routes
  API: {
    // Consolidated messaging endpoints
    MESSAGING: {
      THREADS: '/communications/threads',
      THREAD_MESSAGES: '/communications/threads/:threadId/messages',
      THREAD_READ: '/communications/threads/:threadId/read',
      INQUIRIES: '/communications/investors/inquiries',


      ANALYTICS: '/analytics/summary'
    },
    
    // Investor API endpoints - For backward compatibility
    INVESTOR: {
      // Dashboard
      DASHBOARD: '/investors/dashboard',
      
      // Opportunities
      OPPORTUNITIES_GRID: '/investors/opportunities/grid',
      OPPORTUNITIES: '/investors/opportunities',
      CALENDAR_VIEW: '/investors/opportunities/calendar',
      GEOGRAPHIC_VIEW: '/investors/opportunities/geographic',
      TRENDING: '/investors/opportunities/trending',
      

      
      // Watchlist
      WATCHLIST: '/investors/watchlist',
      WATCHLIST_ITEM: '/investors/watchlist/:id',
      WATCHLIST_ENHANCED: '/investors/watchlist/enhanced',
      WATCHLIST_INSIGHTS: '/investors/watchlist/insights',
      
      // Portfolio Analysis
      PORTFOLIO_BENCHMARKS: '/investors/build_portfolio/benchmarks',
      // Benchmarks (consolidated - use benchmarkService)
      BENCHMARKS: '/benchmarks',
      BENCHMARK_BY_ID: '/benchmarks/:id',
      BENCHMARK_PERFORMANCE: '/benchmarks/:id/performance',
      BENCHMARK_COMPARE: '/benchmarks/:id/compare',
      PORTFOLIO_ENTITIES: '/investors/build_portfolio/entities',
      PORTFOLIO_COMPARE: '/investors/build_portfolio/compare',
      PORTFOLIO_OPTIONS: '/investors/build_portfolio/analysis-options',
      
      // Communication & Engagement
      THREADS: '/communications/threads',
      THREAD_MESSAGES: '/communications/threads/:threadId/messages',
      THREAD_READ: '/communications/threads/:threadId/read',
      INQUIRIES: '/communications/investors/inquiries',
      ENGAGEMENT: '/investors/engagement',
      
      // Settings
      PREFERENCES: '/investors/settings/preferences',
      ALERTS: '/investors/alerts',
      FETCH_COINVEST_DETAILS: '/investors/opportunities/fetch_coinvest_details',

      // Explore V2 (blind teaser)
      OPPORTUNITIES_V2: '/investors/opportunities/v2/search',
      AI_FILTER_V2: '/investors/opportunities/v2/ai-filter',
      REVEAL_REQUESTS: '/investors/opportunities/v2/reveal-request'
    },

    // Manager API endpoints
    MANAGER: {
      PREFERENCES: '/managers/settings/preferences'
    },

    // Admin API endpoints
    ADMIN: {
      /** CRUD for DynamoDB lookup table (Lambda: dev_maintain_lookups); requires LOOKUP_TABLE_NAME on backend */
      MANAGE_LOOKUPS: '/admin/manage_lookups',
      PLATFORM_FEATURES: '/admin/platform_features',
      UTILITIES: '/admin/utilities',
      UTILITIES_PUBLISH_OPPORT: '/admin/utilities/publish-opport',
      UTILITIES_LIST_USERS: '/admin/utilities/list-users',
      UTILITIES_GENERATE_DEAL_SHEETS: '/admin/utilities/generate-deal-sheets',
      REV_SHARE_INVITE: '/admin/utilities/rev-share-invite',
      REV_SHARE_INVITE_UPLOAD_URL: '/admin/utilities/rev-share-invite/upload-url',
      REV_SHARE_INVITE_RESEND: '/admin/utilities/rev-share-invite/resend',
      INVESTOR_INVITE: '/admin/utilities/investor-invite',
      INVESTOR_INVITE_UPLOAD_URL: '/admin/utilities/investor-invite/upload-url',
      INVESTOR_INVITE_RESEND: '/admin/utilities/investor-invite/resend',
      REVEAL_REQUESTS: '/admin/reveal-requests',
      REVEAL_REQUEST_ACTION: (requestId, action) => `/admin/reveal-requests/${requestId}/${action}`,
      MARKETING_TEMPLATES: '/admin/marketing/templates',
      MARKETING_CAMPAIGNS: '/admin/marketing/campaigns',
    }
  },
  
  // Admin routes
  ADMIN_DASHBOARD: '/dashboard/admin',
  ADMIN_ACTIVITY_TRACKING: '/dashboard/admin/activity',
  ADMIN_PERFORMANCE_METRICS: '/dashboard/admin/performance-metrics',
  ADMIN_SETTINGS: '/dashboard/admin/settings',
  ADMIN_SETTINGS_LOOKUPS: '/dashboard/admin/settings/lookups',
  ADMIN_SETTINGS_UTILITIES: '/dashboard/admin/settings/utilities',
  ADMIN_REVEAL_REQUESTS: '/dashboard/admin/settings/reveal-requests',
  ADMIN_OPPORTUNITY_CONTENT_APPROVALS: '/dashboard/admin/settings/opportunity-content-approvals',
  ADMIN_SETTINGS_MANAGE_OPPORTUNITIES: '/dashboard/admin/settings/opportunities',
  ADMIN_PORTAL_ACCESS: '/dashboard/admin/settings/portal-access',
  ADMIN_MANAGERS: '/dashboard/admin/managers',
  ADMIN_ALLOCATORS: '/dashboard/admin/allocators',
  ADMIN_OPPORTUNITIES: '/dashboard/admin/opportunities',
  ADMIN_AGREEMENTS: '/dashboard/admin/agreements',
  ADMIN_COMMUNICATION_SETTINGS: '/dashboard/admin/settings/communication',
  /** @deprecated Use ADMIN_COMMUNICATION_SETTINGS?section=digest */
  ADMIN_DAILY_DIGEST: '/dashboard/admin/settings/daily-digest',
  ADMIN_EXECUTIVE_BRIEF: '/dashboard/admin/executive-brief',
  // ADMIN_BILLING_APPROVALS: '/dashboard/admin/billing/approvals', // Archived - subscription model removed
  ADMIN_MESSAGING: '/dashboard/admin/messaging',
  ADMIN_MESSAGING_THREAD: '/dashboard/admin/messaging/thread/:threadId',
  // Sales — CRM (contacts/pipeline/follow-ups) and Campaigns (email marketing)
  ADMIN_SALES_CRM: '/dashboard/admin/sales/crm',
  ADMIN_SALES_CRM_PIPELINE: '/dashboard/admin/sales/crm/pipeline',
  ADMIN_SALES_CAMPAIGNS: '/dashboard/admin/sales/campaigns',
  ADMIN_SALES_CAMPAIGNS_NEW: '/dashboard/admin/sales/campaigns/new',
  ADMIN_SALES_CAMPAIGNS_CAMPAIGN: '/dashboard/admin/sales/campaigns/:campaignId',
  ADMIN_SALES_CAMPAIGNS_SUPPRESSION: '/dashboard/admin/sales/campaigns/suppression',
  /** @deprecated Use ADMIN_SALES_CRM (contacts/follow-ups) or ADMIN_SALES_CAMPAIGNS (overview/campaigns) */
  ADMIN_MARKETING: '/dashboard/admin/marketing',
  /** @deprecated Use ADMIN_SALES_CAMPAIGNS_NEW */
  ADMIN_MARKETING_NEW: '/dashboard/admin/marketing/new',
  /** @deprecated Use ADMIN_SALES_CAMPAIGNS_CAMPAIGN */
  ADMIN_MARKETING_CAMPAIGN: '/dashboard/admin/marketing/:campaignId',
  /** @deprecated Use ADMIN_SALES_CAMPAIGNS_SUPPRESSION */
  ADMIN_MARKETING_SUPPRESSION: '/dashboard/admin/marketing/suppression',
  MARKETING_PREFERENCES: '/marketing/preferences',
  
  // Registration steps
  REGISTER_BASIC: '/register',
  REGISTER_ROLE: '/register/role',
  REGISTER_MANAGER: '/register/manager',
  REGISTER_INVESTOR: '/register/investor',
  REGISTER_ADMIN: '/register/admin',
  REGISTER_COMPLETE: '/register/complete',
  
  // Helper functions for dynamic routes
  getDashboardForRole: (role) => {
    switch (role) {
      case 'manager': return '/dashboard/manager';
      case 'investor': return '/dashboard/investor/explore';
      case 'admin': return '/dashboard/admin';
      default: return '/dashboard';
    }
  },
  
  getProductDetailsRoute: (productId) => `/dashboard/manager/products/${productId}`,
  
  getProductEditRoute: (productId) => `/dashboard/manager/products/${productId}/edit`,
  
  getInvestorDetailsRoute: (investorId) => `/dashboard/manager/investor-relations/${investorId}`,
  
  getInvestorMeetingRoute: (meetingId) => `/dashboard/manager/investor-relations/meetings/${meetingId}`,
  
  getInvestorDocumentRoute: (documentId) => `/dashboard/manager/investor-relations/documents/${documentId}`,
  
  getInvestorFundDetailsRoute: (fundId) => `/dashboard/investor/explore/funds/${fundId}`,
  
  getAdminUserDetailsRoute: (userId) => `/dashboard/admin/users/${userId}`,
  
  getAdminFirmDetailsRoute: (firmId) => `/dashboard/admin/firms/${firmId}`,
  
  getAdminFundDetailsRoute: (fundId) => `/dashboard/admin/funds/${fundId}`,
  
  getOpportunityDetailsRoute: (id) => `/dashboard/investor/opportunities/${id}`,
  
  getCoInvestmentEditRoute: (id) => `/dashboard/manager/coinvestments/${id}`,
  
  // API path helper functions for investor routes
  getInvestorOpportunityDetailsPath: (id) => `/investors/opportunities/fetch_coinvest_details?co_invest_id=${id}`,
  getThreadMessagesPath: (threadId) => `/communications/threads/${threadId}/messages`,
  getThreadReadPath: (threadId) => `/communications/threads/${threadId}/read`,
  getWatchlistItemPath: (opportunityId) => `/investors/watchlist/${opportunityId}`,
  
  // Order route helpers
  getInvestorOrderDetailsRoute: (orderId) => `/dashboard/investor/orders/${orderId}`,
  getManagerOrderDetailsRoute: (orderId) => `/dashboard/manager/orders/${orderId}`,
  getAdminOrderDetailsRoute: (orderId) => `/dashboard/admin/orders/${orderId}`,

};


export default ROUTES;